'use client'
import { cn } from '@/util/cn'
import useClientRender from '@/util/use-client-render'
import { IChannel, IUser } from '@giphy/js-types'
import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useKeyPress } from 'react-use'
import { CloseIcon } from 'ui/src/icons/utility'
import NavigateLeftIcon from '../icons/navigate-left'

export type ModalType =
    | { name: 'createCollection'; title?: ReactNode; channelId?: IChannel['id'] }
    | { title?: ReactNode; name: 'addToCollection'; channelId?: IChannel['id'] }
    | { name: 'hireMe'; title?: ReactNode; artist: IUser; channelId?: IChannel['id'] }
    | { name: 'deactivateUser'; title?: ReactNode; channelId?: IChannel['id']; channel: IChannel }
    | { name: 'derankUser'; title?: ReactNode; channelId?: IChannel['id']; channel: IChannel }
    | { name: 'editCollection'; channelId?: IChannel['id']; channel: IChannel; title?: ReactNode }
    | { name: 'editCollections'; channelId?: IChannel['id']; title?: ReactNode }
    | { name: 'addGifsToCollection'; channel: IChannel; channelId: IChannel['id']; title?: string }
    | null

type Props = {
    children: ReactNode
    title: ReactNode
    titleBarComponent?: ReactNode
    previousModalId?: ModalType | null
    className?: string
    contentClassName?: string
    goBack?: () => void
    onClose?: () => void
}

type ModalContextType = { modal: ModalType; setModalType: Dispatch<SetStateAction<ModalType>>; goBack: () => void }
export const ModalContext = createContext<ModalContextType>({ modal: null, setModalType: () => {}, goBack: () => {} })

export const modalClassName = `bg-giphyBlack/80 z-modal fixed inset-0 flex items-center justify-center`
export const modalBoxClassName = 'bg-giphyDarkestGrey flex min-h-[390px] w-5/6 max-w-[838px] flex-col p-3'
export function Modal({
    children,
    title,
    previousModalId = null,
    className,
    contentClassName,
    goBack,
    onClose,
    titleBarComponent,
}: Props) {
    const isClient = useClientRender()
    const [isEsc] = useKeyPress('Escape')
    useEffect(() => {
        if (isEsc) {
            onClose?.()
        }
    }, [isEsc, onClose])
    return isClient
        ? createPortal(
              <div className={modalClassName}>
                  <div className={cn(modalBoxClassName, className)}>
                      <div className="flex flex-col">
                          <div className="relative isolate flex items-center justify-between gap-2">
                              {previousModalId && (
                                  <div
                                      className="cursor-pointer"
                                      onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          goBack?.()
                                      }}
                                  >
                                      <NavigateLeftIcon className="size-4" />
                                  </div>
                              )}
                              <h1 className="relative box-border flex max-h-[23px] max-w-[400px] flex-row overflow-hidden text-base whitespace-nowrap select-none">
                                  {title}
                              </h1>
                              {titleBarComponent}
                              {onClose && (
                                  <div
                                      onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          onClose?.()
                                      }}
                                      className="relative cursor-pointer"
                                  >
                                      <CloseIcon className="size-4" />
                                  </div>
                              )}
                          </div>
                          <hr className="my-3 w-full border-giphyDarkGrey" />
                      </div>
                      <div className={cn('overflow-auto', contentClassName)}>{children}</div>
                  </div>
              </div>,
              document.querySelector('body')!
          )
        : null
}

// useful for server components
export const ClickToShowModal = ({
    children,
    modal,
    className,
}: {
    modal: ModalType
    children: ReactNode
    className?: string
}) => {
    const { setModalType } = useContext(ModalContext)

    return (
        <div
            className={className}
            onClick={async (e) => {
                e.preventDefault()
                e.stopPropagation()
                setModalType(modal)
            }}
        >
            {children}
        </div>
    )
}
